import Information16 from '@carbon/icons-react/es/information/16';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { FilterBox } from 'components/molecules/filters/filter-box';
import { Filter } from 'components/molecules/filters/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  GetFilterOperationTypeOptions,
  GetSelectEditorComponents,
} from 'components/organisms/tasks/filter-box/utils';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { Caption01 } from 'components/styles';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { HoverableLinkButton } from '../components';
import { FilterPopoverDone } from './filter-popover-done';

// :TODO reuse from molecules/filter
export interface Props {
  loading: boolean;
  filter: Filter;
  fields: FilterableField[];
  onChange: (filter: Filter) => void;
  onVisibilityChange?: (visible: boolean) => void;
  getSelectEditorComponents?: GetSelectEditorComponents;
  getFilterOperationTypeOptions?: GetFilterOperationTypeOptions;
}

export const FilterBoxWrapper = styled.div`
  padding: var(--spacing-04) var(--spacing-05);
`;

export const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 var(--spacing-05) var(--spacing-03);
  ${Caption01};
  color: var(--gray-50);
`;

const StyledHoverableLinkButton = styled(HoverableLinkButton)`
  border-color: var(--primary-color);
`;

export const FilterPopover: FC<Props> = ({
  loading,
  filter,
  fields,
  onChange,
  onVisibilityChange,
  getSelectEditorComponents,
  getFilterOperationTypeOptions,
}) => {
  const [visible, setVisible] = useState(false);
  const translate = useTranslate();
  const isDashBoards = window.location.pathname.includes('/dashboards');
  const isChild = isChildNode([
    'filter-box',
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
    'ant-popover-inner-content',
    'ozone-select__indicator',
  ]);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  return (
    <Popover
      content={
        <>
          <FilterBoxWrapper>
            <FilterBox
              fields={fields}
              filter={filter}
              onChange={onChange}
              getFilterOperationTypeOptions={getFilterOperationTypeOptions}
              getSelectEditorComponents={getSelectEditorComponents}
            />
          </FilterBoxWrapper>
          <FilterPopoverDone onClick={() => setVisible(false)} />
          {isDashBoards ? (
            <InfoWrapper>
              <Information16 />
              {translate('dashboard.filter.popover.info')}
            </InfoWrapper>
          ) : null}
        </>
      }
      trigger={['click']}
      placement="bottomLeft"
      visible={visible}
      destroyTooltipOnHide
    >
      <OutsideClickHandler
        allowed={isChild}
        onOutsideClick={() => setVisible(false)}
      >
        <StyledHoverableLinkButton
          data-cy="buttons.filter-button"
          onClick={() => setVisible(!visible)}
          loading={loading}
        >
          {useTranslation('captions.buttons.filter', {
            count: filter.criteria.filter(FieldFilter.isValid).length,
          })}
        </StyledHoverableLinkButton>
      </OutsideClickHandler>
    </Popover>
  );
};
